import { Box } from '@mentimeter/ragnar-ui/box';
import { postCreateRequest } from '@core-api/participant-rating/participant-rating/create/{vote_key}';
import { SendingIllustration } from '@mentimeter/ragnar-visuals/spotlight';
import React from 'react';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Button, Form } from '@mentimeter/ragnar-form';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { StarSolidIcon } from '@mentimeter/ragnar-visuals';
import type { Regions, TrackVoter } from '@mentimeter/http-clients';
import { BottomPanel } from '@mentimeter/voting-ui';
import { useRagnar } from '@mentimeter/ragnar-react';
import { getRegionByVoteKey } from '@mentimeter/region';

type RatingValue = 1 | 2 | 3 | 4 | 5;
interface Rating {
  id: number;
  value: RatingValue;
  label: string;
}

const starRatings: Rating[] = [
  { id: 1, value: 1, label: 'Hard to stay focused' },
  { id: 2, value: 2, label: 'Some sparks here and there' },
  { id: 3, value: 3, label: 'Pretty decent' },
  { id: 4, value: 4, label: 'Nicely done!' },
  { id: 5, value: 5, label: 'Spot on!' },
];

export const BeesParticipantRating = ({
  voteKey,
  version,
  trackVoter,
}: {
  voteKey: string;
  version: 'v1' | 'v2';
  trackVoter: (data: Parameters<TrackVoter>[0], region?: Regions) => any;
}) => {
  const [submittedRating, setSubmittedRating] = React.useState(false);
  const [selectedRating, setSelectedRating] = React.useState({
    id: 0,
    value: 0,
    label: 'How engaging was this Menti?',
  });
  const [show, setShow] = React.useState(true);

  const handleClose = React.useCallback(() => {
    setShow(false);
  }, [setShow]);
  const { theme } = useRagnar();
  const region = getRegionByVoteKey(voteKey);

  const content = (
    <Box alignItems="center">
      {!submittedRating ? (
        <>
          <Text>{selectedRating.label}</Text>
          <Form
            initialValues={{ rating: null }}
            onSubmit={async () => {
              await fetch(
                postCreateRequest({
                  region,
                  voteKey,
                  rating: selectedRating.value as RatingValue,
                }),
              );

              trackVoter({
                event: 'Clicked submit feedback',
                properties: {
                  rating: selectedRating.value,
                  version,
                },
              });
              setSubmittedRating(true);
              if (handleClose) {
                setTimeout(handleClose, 2000);
              }
            }}
          >
            <Box mb="space6" mt="space4" flexDirection="row" gap="space3">
              {starRatings.map((rating, index) => {
                const inSelectedRange =
                  selectedRating && index < selectedRating.value;
                return (
                  <IconButton
                    key={rating.id}
                    variant="tertiary"
                    bg={inSelectedRange ? 'neutral' : 'infoWeakest'}
                    type="button"
                    onClick={() => {
                      trackVoter({
                        event: 'Clicked star',
                        properties: {
                          rating: rating.value,
                          version,
                        },
                      });
                      setSelectedRating(rating);
                    }}
                  >
                    <StarSolidIcon
                      color={inSelectedRange ? 'primary' : 'neutralWeak'}
                    />
                  </IconButton>
                );
              })}
            </Box>
            {selectedRating.value !== 0 && (
              <Box width="100%" alignItems="center">
                <Text mb="space4" color="textWeaker">
                  Rate the engagement
                </Text>
                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </Box>
            )}
          </Form>
        </>
      ) : (
        <Box alignItems="center" width="100%" mb="space6">
          <Box width="100px" my="space4">
            <SendingIllustration />
          </Box>
          <Text color="text" textAlign="center">
            Thank you!
          </Text>
          <Text color="text" textAlign="center">
            Your feedback is on its way.
          </Text>
        </Box>
      )}
    </Box>
  );

  return version === 'v1' ? (
    content
  ) : (
    <BottomPanel show={show} onDismiss={handleClose} themeBg={theme.colors.bg}>
      <Box
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="40vh"
      >
        {content}
      </Box>
    </BottomPanel>
  );
};
